import { Session } from "@/types/auth"
import { makeOrganization, OrganizationContext } from "@/types/organization"
import { PublicPageProps } from "@/types/page"
import { LastUsedProviders, type LastUsedProvider } from "@/types/user"
import { createAsyncThunk } from "@reduxjs/toolkit"

import { setAuthTokenExpiration } from "@/lib/auth/client"
import { mtLocation } from "@/lib/mt-location"
import { earlyAccessApi } from "@/lib/storage/api/early-access-api"
import { featureFlagApi } from "@/lib/storage/api/feature-flag-api"
import {
  setEmbedded,
  setEnableImpressions,
  setOrgProps,
} from "@/lib/storage/app-slice"
import { setLastUsedProvider, setSession } from "@/lib/storage/auth-slice"
import { initAppleSignIn, initGoogleSignIn } from "@/lib/storage/auth-thunks"
import { finishPageInitCycle } from "@/lib/storage/page-init-gate"
import { AppDispatch, RootState } from "@/lib/storage/redux-store"
import {
  ChatBotVersionKey,
  ChatBotVersionKeys,
  setChatBotVersion,
} from "@/lib/storage/ui-settings-slice"
import userSlice from "@/lib/storage/user-slice"
import { webStorage } from "@/lib/web-storage"
import { showPostSignupPromptsIfNecessary } from "./post-signup-thunks"

// Once per document: work that does not depend on which page is showing.
export const initApp = createAsyncThunk<
  void,
  PublicPageProps,
  { dispatch: AppDispatch; state: RootState }
>("authThunks/initApp", async (initProps, { dispatch }) => {
  dispatch(setEmbedded(initProps.isEmbedded))
  dispatch(userSlice.actions.setBrowser(initProps.browser))
  dispatch(setEnableImpressions(!!initProps.enableImpressions))

  // Only a visitor without an account can meet the auth dialog or the register gate, so warm
  // the provider SDKs for them once the page's own resources have finished (the load event;
  // Safari has no requestIdleCallback), and the buttons render without their "Initializing…"
  // wait. Signed-in users never load them.
  const anonymous =
    !initProps.session || initProps.session.user.provider === "anonymous"
  if (anonymous) {
    const warmSignInSdks = () => {
      void dispatch(initAppleSignIn())
      void dispatch(initGoogleSignIn())
    }
    if (document.readyState === "complete") {
      warmSignInSdks()
    } else {
      window.addEventListener("load", warmSignInSdks, { once: true })
    }
  }

  // Handle ?bot= URL parameter to override bot selection
  const urlParams = new URLSearchParams(mtLocation.search)
  const botParam = urlParams.get("bot")
  if (botParam && ChatBotVersionKeys.includes(botParam as ChatBotVersionKey)) {
    dispatch(setChatBotVersion(botParam as ChatBotVersionKey))
  }

  // Initialize last used provider from storage
  const storedProvider = webStorage.getItem("mindtrip.last_used_provider")
  if (
    storedProvider &&
    LastUsedProviders.includes(storedProvider as LastUsedProvider)
  ) {
    dispatch(setLastUsedProvider(storedProvider as LastUsedProvider))
  }
})

/**
 * The session, its user and permissions, in the store. Synchronous, so the
 * store can start with them and a page renders signed in on the server.
 */
export function storeSession(dispatch: AppDispatch, session: Session) {
  dispatch(setSession(session))
  dispatch(userSlice.actions.setUser(session.user))
  dispatch(userSlice.actions.setUserPermissions(session.permissions))
}

// Per page landing: idempotent, so it is safe to run on every navigation.
// Props a soft navigation omits (see MtSSP) leave the store as it is.
export const applyPageProps = createAsyncThunk<
  void,
  PublicPageProps,
  { dispatch: AppDispatch; state: RootState }
>("authThunks/applyPageProps", async (initProps, { dispatch }) => {
  try {
    setAuthTokenExpiration(initProps.session?.expires_in)

    if (initProps.session) {
      storeSession(dispatch, initProps.session)

      if (
        initProps.session.user.provider &&
        initProps.session.user.provider !== "anonymous"
      ) {
        webStorage.setItem(
          "mindtrip.last_used_provider",
          initProps.session.user.provider
        )
        dispatch(setLastUsedProvider(initProps.session.user.provider))
      }
    }

    // Seed the enabled-flag list from the MtSSP gate so gated pages don't
    // refetch it client-side. Non-gated pages fetch lazily via useFeatureFlag.
    if (initProps.enabledFeatureFlags) {
      void dispatch(
        featureFlagApi.util.upsertQueryData(
          "getFeatureFlags",
          undefined,
          initProps.enabledFeatureFlags
        )
      )
    }

    // Seed early-access flags the same way, so pages that fetch them server-side
    // (e.g. settings) render flag-gated UI without a client-fetch reflow.
    if (initProps.earlyAccessFlags) {
      void dispatch(
        earlyAccessApi.util.upsertQueryData(
          "getEarlyAccessFeatureFlags",
          undefined,
          initProps.earlyAccessFlags
        )
      )
    }

    let orgContext: OrganizationContext | undefined = undefined
    if (initProps.organization) {
      orgContext = makeOrganization(initProps.organization, {
        orgid: initProps.orgid,
        orgUrl: initProps.orgUrl,
        geoName: initProps.geoName,
      })
    }

    dispatch(
      setOrgProps({
        organization: initProps.organization,
        organizationContext: orgContext,
        orgid: initProps.orgid,
        orgUrl: initProps.orgUrl,
        geoName: initProps.geoName,
      })
    )

    // Landing on a page is what follows a signup, so this is where a new user
    // gets asked for their name and to verify their email.
    void dispatch(showPostSignupPromptsIfNecessary())
  } finally {
    finishPageInitCycle()
  }
})

export const updateSessionFromRefresh = createAsyncThunk<
  void,
  Session,
  { dispatch: AppDispatch; state: RootState }
>("authThunks/updateSessionFromRefresh", async (session, { dispatch }) => {
  try {
    dispatch(setSession(session))
    dispatch(userSlice.actions.setUser(session.user))
    dispatch(userSlice.actions.setUserPermissions(session.permissions))
    dispatch(featureFlagApi.util.invalidateTags(["FeatureFlag"]))
  } catch (error) {
    console.error("Failed to update session after refresh:", error)
    throw error
  }
})
